/**
 * ActionChip Component
 * Reusable action button chip for workspace
 * Shows LOADING STATE when processing
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronUp, ChevronDown, Loader2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getWorkspaceColors } from './styles';

const ActionChip = ({ 
  icon: Icon,
  label,
  isActive,
  isDisabled,
  isProcessing = false,
  processingLabel = 'Processando...',
  hasDropdown,
  isDropdownOpen,
  onClick,
  badge,
  tooltip,
}) => {
  const { colors, theme } = useTheme();
  const workspaceColors = getWorkspaceColors(colors, theme === 'dark');
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);

  // Determine if chip should appear disabled
  const effectivelyDisabled = isDisabled || isProcessing;

  // Update tooltip position
  useEffect(() => {
    if (!showTooltip || !buttonRef.current) return;

    const updatePosition = () => {
      const rect = buttonRef.current.getBoundingClientRect();
      setTooltipPosition({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    };

    updatePosition();
  }, [showTooltip]);

  const handleClick = (e) => {
    if (effectivelyDisabled) return;
    onClick?.(e);
  };

  const chipStyles = {
    container: {
      position: 'relative',
    },
    button: {
      padding: '10px 18px',
      borderRadius: 24,
      border: 'none',
      backgroundColor: isProcessing
        ? workspaceColors.bgSecondary
        : isActive 
          ? workspaceColors.accentPrimary 
          : (workspaceColors.chipBg || workspaceColors.bgTertiary),
      color: isProcessing
        ? workspaceColors.textSecondary
        : isActive 
          ? 'white' 
          : workspaceColors.textPrimary,
      cursor: effectivelyDisabled ? 'not-allowed' : 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      fontSize: 14,
      fontWeight: 600,
      transition: 'all 0.15s ease',
      opacity: isDisabled && !isProcessing ? 0.5 : 1,
      position: 'relative',
    },
    badge: {
      position: 'absolute',
      top: -4,
      right: -4,
      minWidth: 20,
      height: 20,
      padding: '0 6px',
      borderRadius: 10,
      backgroundColor: isActive ? 'white' : workspaceColors.accentPrimary,
      color: isActive ? workspaceColors.accentPrimary : 'white',
      fontSize: 11,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    spinner: {
      animation: 'actionChipSpin 1s linear infinite',
    },
  };

  const tooltipStyles = {
    container: {
      position: 'fixed',
      top: tooltipPosition.top,
      left: tooltipPosition.left,
      transform: 'translate(-50%, -100%)',
      backgroundColor: workspaceColors.bgPrimary,
      color: workspaceColors.textPrimary,
      padding: '8px 12px',
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 500,
      whiteSpace: 'nowrap',
      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: workspaceColors.borderColor,
      zIndex: 10000,
      pointerEvents: 'none',
    },
    arrow: {
      position: 'absolute',
      bottom: -6,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '6px solid transparent',
      borderRight: '6px solid transparent',
      borderTop: `6px solid ${workspaceColors.borderColor}`,
    },
  };

  // Render tooltip via Portal
  const renderTooltip = () => {
    if (!showTooltip || !isDisabled || !tooltip || isProcessing) return null;

    return createPortal(
      <div style={tooltipStyles.container}>
        {tooltip}
        <div style={tooltipStyles.arrow} />
      </div>,
      document.body
    );
  };

  // Determine what to show
  const showLabel = isProcessing ? processingLabel : label;
  const showIcon = isProcessing ? null : Icon;

  return (
    <div 
      style={chipStyles.container}
      onMouseEnter={() => isDisabled && !isProcessing && tooltip && setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <button
        ref={buttonRef}
        style={chipStyles.button}
        onClick={handleClick}
        disabled={effectivelyDisabled}
        onMouseEnter={(e) => {
          if (!isActive && !effectivelyDisabled) {
            e.currentTarget.style.backgroundColor = workspaceColors.chipHover || workspaceColors.bgSecondary;
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive && !effectivelyDisabled && !isProcessing) {
            e.currentTarget.style.backgroundColor = workspaceColors.chipBg || workspaceColors.bgTertiary;
          }
        }}
      >
        {/* Spinner when processing */}
        {isProcessing && <Loader2 size={18} style={chipStyles.spinner} />}
        
        {/* Icon when not processing */}
        {showIcon && <Icon size={18} />}
        
        {/* Label */}
        {showLabel}
        
        {/* Dropdown chevron (only when not processing) */}
        {hasDropdown && !isProcessing && (
          isDropdownOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />
        )}
        
        {/* Badge (only when not processing) */}
        {!isProcessing && badge != null && badge > 0 && (
          <span style={chipStyles.badge}>{badge}</span>
        )}
      </button>

      {/* Tooltip via Portal */}
      {renderTooltip()}

      <style>{`
        @keyframes actionChipSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ActionChip;